import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ChevronLeft, Search, UserRound, Blocks, Clock, MonitorSmartphone, Pencil, Trash2 } from 'lucide-react';
import { v4 } from 'uuid';
import { useAlertContext } from '../contexts/Alert';
import { USER_URL, ADMIN_URL } from '../api_base.js';
import { buildHeaders } from '../utils/apiHeaders.js';

const PAGE_SIZE = 10;

// A user counts as "online" if they've logged in within this window.
// This is a client-side approximation from lastLoginAt — it is not a live
// presence signal, since the backend doesn't track session heartbeats.
const ONLINE_WINDOW_MS = 15 * 60 * 1000;

const isOnline = (lastLoginAt) => lastLoginAt && (Date.now() - lastLoginAt) < ONLINE_WINDOW_MS;

const timeAgo = (ts) => {
    if (!ts) return "Never";
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [deviceCounts, setDeviceCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [editUser, setEditUser] = useState(null);
    const [deleteUser, setDeleteUser] = useState(null);
    const [busy, setBusy] = useState(false);

    const firstUserDate = useRef(Date.now());
    const { role } = useOutletContext() || {};
    const isAdmin = role === "admin";

    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => setAlerts(prev => ([...prev, { id: v4(), text }]));

    const fetchDeviceCount = async (userId) => {
        try {
            const res = await fetch(ADMIN_URL + "/users/" + userId + "/devices", {
                method: "GET",
                credentials: "include",
                headers: buildHeaders(),
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setDeviceCounts(prev => ({ ...prev, [userId]: (data.data || []).length }));
        } catch (err) {
            // Endpoint may not exist yet on the backend — degrade gracefully.
            setDeviceCounts(prev => ({ ...prev, [userId]: null }));
        }
    }

    const fetchPage = async (pageNumber, append) => {
        try {
            const params = new URLSearchParams({
                pageNumber: String(pageNumber),
                firstUserDate: String(firstUserDate.current),
            });

            const res = await fetch(ADMIN_URL + "/users?" + params.toString(), {
                method: "GET",
                credentials: "include",
                headers: buildHeaders(),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                if (data.signout) navigate("/auth");
                pushAlert(data.message || "Oops! something went wrong while loading users.");
                return;
            }

            const items = data.data || [];
            setUsers(prev => append ? [...prev, ...items] : items);
            setHasMore(items.length === PAGE_SIZE);
            items.forEach(u => fetchDeviceCount(u.userId));
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        }
    }

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchPage(1, false);
            setLoading(false);
        })();
    }, []);

    const loadMore = async () => {
        setLoadingMore(true);
        const nextPage = page + 1;
        await fetchPage(nextPage, true);
        setPage(nextPage);
        setLoadingMore(false);
    }

    const filteredUsers = users.filter(u =>
        !search.trim() ||
        u.username?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    const saveEdit = async (role, accountStatus) => {
        setBusy(true);
        try {
            const res = await fetch(ADMIN_URL + "/users", {
                method: "PATCH",
                credentials: "include",
                headers: buildHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ userId: editUser.userId, userData: { role, accountStatus } }),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while updating this user.");
                return;
            }

            setUsers(prev => prev.map(u => u.userId === editUser.userId ? { ...u, role, accountStatus } : u));
            pushAlert("User updated successfully.");
            setEditUser(null);
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setBusy(false);
        }
    }

    const confirmDelete = async () => {
        setBusy(true);
        try {
            const res = await fetch(ADMIN_URL + "/users", {
                method: "DELETE",
                credentials: "include",
                headers: buildHeaders({ "Content-Type": "application/json" }),
                body: JSON.stringify({ userId: deleteUser.userId }),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                pushAlert(data.message || "Oops! something went wrong while removing this user.");
                return;
            }

            setUsers(prev => prev.filter(u => u.userId !== deleteUser.userId));
            pushAlert("User removed successfully.");
            setDeleteUser(null);
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="page">
            <div className="page_header">
                <button className="back" onClick={() => navigate("/admin")}><ChevronLeft /></button>
                <div className="page_title">
                    <h2>Users</h2>
                    <p>{users.length} loaded · manage accounts and access.</p>
                </div>
            </div>

            <div className="page_body">
                {!isAdmin ? (
                    <div className="viewer_note">Viewer mode — you can browse user details, but editing and removal are disabled.</div>
                ) : null}

                <div className="toolbar">
                    <div className="search_box">
                        <Search size="16" />
                        <input placeholder="Search by username or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>

                {loading ? (
                    <div className="empty_state"><span className="loading"></span></div>
                ) : filteredUsers.length === 0 ? (
                    <div className="empty_state">
                        <UserRound size="40" />
                        <h3>No users found</h3>
                    </div>
                ) : (
                    <>
                        <div className="admin_hub">
                            {filteredUsers.map(u => {
                                const online = isOnline(u.lastLoginAt);
                                const deviceCount = deviceCounts[u.userId];
                                return (
                                    <div className="user_card" key={u.userId}>
                                        <div className="user_card_top">
                                            <div className="user_avatar">
                                                {u.avatar ? <img src={u.avatar} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : <UserRound size="20" />}
                                                <span className={`presence_dot ${online ? "" : "offline"}`} title={online ? "Online" : "Offline"}></span>
                                            </div>
                                            <div className="user_card_meta">
                                                <h4>{u.username}</h4>
                                                <span>{u.email}</span>
                                            </div>
                                        </div>

                                        <div className="badge_row">
                                            <span className="badge">{u.role}</span>
                                            <span className={`badge ${u.accountStatus}`}>{u.accountStatus}</span>
                                        </div>

                                        <div className="user_card_stats">
                                            <div className="mini_stat">
                                                <span className="mini_stat_label"><Blocks size="10" /> Apps</span>
                                                <span className="mini_stat_value">{u.linkedApps?.length ?? 0}</span>
                                            </div>
                                            <div className="mini_stat">
                                                <span className="mini_stat_label"><MonitorSmartphone size="10" /> Devices</span>
                                                <span className="mini_stat_value">{deviceCount === null ? "—" : deviceCount ?? "…"}</span>
                                            </div>
                                            <div className="mini_stat">
                                                <span className="mini_stat_label"><Clock size="10" /> Last Seen</span>
                                                <span className="mini_stat_value">{timeAgo(u.lastLoginAt)}</span>
                                            </div>
                                        </div>

                                        {isAdmin ? (
                                            <div className="user_card_actions">
                                                <button className="pill_button ghost" onClick={() => setEditUser(u)}>
                                                    <Pencil size="14" /> Edit
                                                </button>
                                                <button className="pill_button danger" onClick={() => setDeleteUser(u)}>
                                                    <Trash2 size="14" /> Remove
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                )
                            })}
                        </div>

                        {hasMore ? (
                            <div className="load_more_row">
                                <button className="pill_button ghost" disabled={loadingMore} onClick={loadMore}>
                                    {loadingMore ? <span className="loading"></span> : "Load more"}
                                </button>
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            {editUser ? (
                <EditUserModal
                    user={editUser}
                    busy={busy}
                    onCancel={() => setEditUser(null)}
                    onSave={saveEdit}
                />
            ) : null}

            {deleteUser ? (
                <div className="modal_backdrop" onClick={() => !busy && setDeleteUser(null)}>
                    <div className="modal_card" onClick={(e) => e.stopPropagation()}>
                        <h3>Remove {deleteUser.username}?</h3>
                        <p style={{ color: "#ffffff90", fontSize: 13 }}>This permanently deletes their account and cannot be undone.</p>
                        <div className="modal_actions">
                            <button className="pill_button ghost" onClick={() => setDeleteUser(null)} disabled={busy}>Cancel</button>
                            <button className="pill_button danger" onClick={confirmDelete} disabled={busy}>
                                {busy ? <span className="loading"></span> : "Remove"}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

const EditUserModal = ({ user, busy, onCancel, onSave }) => {
    const [role, setRole] = useState(user.role);
    const [accountStatus, setAccountStatus] = useState(user.accountStatus);

    return (
        <div className="modal_backdrop" onClick={() => !busy && onCancel()}>
            <div className="modal_card" onClick={(e) => e.stopPropagation()}>
                <h3>Edit {user.username}</h3>
                <div className="field_container">
                    <label>Role</label>
                    <select className="select_input" value={role} onChange={(e) => setRole(e.target.value)}>
                        <option value="user">User</option>
                        <option value="viewer">Viewer</option>
                        <option value="admin">Admin</option>
                    </select>
                </div>
                <div className="field_container">
                    <label>Account Status</label>
                    <select className="select_input" value={accountStatus} onChange={(e) => setAccountStatus(e.target.value)}>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="banned">Banned</option>
                    </select>
                </div>
                <div className="modal_actions">
                    <button className="pill_button ghost" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button className="pill_button" onClick={() => onSave(role, accountStatus)} disabled={busy}>
                        {busy ? <span className="loading"></span> : "Save"}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default AdminUsers;
